import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Alert, Typography, Spin } from 'antd';
import { LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import authService from '@/services/auth.service';

const { Text } = Typography;

const PASSWORD_RULES = [
  { required: true, message: 'Please enter a new password' },
  { min: 8, message: 'At least 8 characters' },
  {
    pattern: /[A-Z]/,
    message: 'At least one uppercase letter',
  },
  {
    pattern: /[a-z]/,
    message: 'At least one lowercase letter',
  },
  {
    pattern: /[0-9]/,
    message: 'At least one number',
  },
  {
    pattern: /[^A-Za-z0-9]/,
    message: 'At least one special character',
  },
];

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token')?.trim() ?? '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ok = await authService.validateResetToken(token);
        if (!cancelled) {
          setTokenValid(ok);
        }
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onFinish = async (values: { newPassword: string }) => {
    try {
      setLoading(true);
      setError(null);
      await authService.resetPassword(token, values.newPassword);
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      setError(ax.response?.data?.message || ax.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-form-surface" style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
          Validating reset link…
        </Text>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="auth-form-surface">
        <Alert
          type="error"
          showIcon
          message="Invalid or expired link"
          description="This password reset link is invalid or has expired. Request a new link from the sign-in page."
          style={{ marginBottom: 24 }}
        />
        <Link to="/forgot-password" className="auth-forgot-link" style={{ float: 'none' }}>
          Request new reset link
        </Link>
        <br />
        <Link to="/login" className="auth-forgot-link" style={{ float: 'none', marginTop: 12 }}>
          <ArrowLeftOutlined /> Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="auth-form-surface">
      {error && (
        <Alert
          className="auth-alert-error"
          message={error}
          type="error"
          closable
          onClose={() => setError(null)}
          showIcon
          role="alert"
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
        <Form.Item name="newPassword" label="New password" rules={PASSWORD_RULES} hasFeedback>
          <Input.Password
            prefix={<LockOutlined aria-hidden />}
            placeholder="New password"
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm password"
          dependencies={['newPassword']}
          hasFeedback
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined aria-hidden />}
            placeholder="Confirm password"
            autoComplete="new-password"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            Reset password
          </Button>
        </Form.Item>
      </Form>

      <Link to="/login" className="auth-forgot-link" style={{ float: 'none' }}>
        <ArrowLeftOutlined /> Back to sign in
      </Link>
    </div>
  );
};

export default ResetPasswordPage;
